import { FlowButton } from 'components/ui/flow-button';
import { speakerDisplayName } from 'lib/speakerPresentation';
import { resolveCourseDetailPresentation } from 'lib/coursePresentation';
import 'components/CoursePresentation.css';
import './CourseDetailPage.css';
import './CourseThemeVariants.css';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, BarChart3, Check, Clock, GraduationCap, Lock, Users, X } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'components/ui/accordion';
import { CourseThumb, GridGlow, Watermark, fadeUp, staggerContainer, viewportOnce } from 'components/MagellanPrototypePrimitives';
import { COURSE_CATEGORIES, getPublicCourseBySlug, listPublicCourses } from 'lib/siteCoursesApi';
import { watchCourseContentChanges } from 'lib/courseContentChanges';
import {
  WEBINAR_FALLBACK_IMAGE,
  getInitials,
  getWebinarSlug,
} from 'lib/magellanDisplayData';

function splitList(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  return String(value || '')
    .split(/\n|;/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function CourseCounterRail({ current, total, course }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.85 }}
      className="course-detail-counter mt-10 grid max-w-xl grid-cols-[auto_minmax(0,1fr)] gap-5 border-y border-ink/10 py-5"
      data-testid="course-detail-counter-rail"
    >
      <div className="font-serif text-6xl leading-none md:text-7xl" style={{ color: `color-mix(in srgb, ${course.accent} var(--accent-strength, 100%), black)` }}>
        {String(current).padStart(2, '0')}
      </div>
      <div className="min-w-0">
        <div className="mb-3 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-tone-500">
          <span>Course outline</span>
          <span>{String(current).padStart(2, '0')} / {String(total).padStart(2, '0')}</span>
        </div>
        <div className="grid h-1.5 grid-cols-10 gap-1">
          {Array.from({ length: Math.max(1, Math.min(10, total)) }).map((_, index) => (
            <span
              key={index}
              className="rounded-full"
              style={{
                backgroundColor: index < current ? course.accent : 'rgb(var(--site-ink-rgb) / .16)',
              }}
            />
          ))}
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-tone-500">
          <span>{course.moduleCount} outline sections</span>
          <span>{course.lessonCount} topics</span>
          <span>{course.isDemo ? 'Demo' : 'Outline'}</span>
        </div>
      </div>
    </motion.div>
  );
}

function ThemeSignalStrip({ course }) {
  const items = [
    { label: course.theme.signalLabel, value: course.visualTemplate },
    { label: 'Instructor', value: course.instructor.name },
    { label: 'Category', value: course.categoryLabel },
    { label: 'Access', value: 'Outline preview' },
  ];
  const dataRoom = course.theme.key === 'data-room';
  const process = course.theme.key === 'process-index';

  return (
    <section className="course-detail-signals border-y border-ink/10" data-testid="course-detail-theme-strip">
      <motion.div
        variants={staggerContainer}
        initial="hidden"
        whileInView="show"
        viewport={viewportOnce}
        className={`site-content-shell ${process ? 'grid grid-cols-1 gap-0 md:grid-cols-4' : 'grid grid-cols-2 gap-y-6 lg:grid-cols-4'}`}
      >
        {items.map((item, index) => (
          <motion.div
            key={item.label}
            variants={fadeUp}
            className={`${process ? 'border-b border-ink/10 py-8 md:border-b-0 md:border-r md:last:border-r-0' : 'py-8 pr-6 lg:border-r lg:border-ink/10 lg:last:border-0'}`}
          >
            <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.25em] text-tone-500">{item.label}</div>
            <div className={`${dataRoom ? 'font-mono text-lg' : 'font-serif text-lg lg:text-xl'} text-ink`}>
              {index === 0 ? String(item.value || '').replace(/-/g, ' ') : item.value}
            </div>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
}

function InstructorSection({ course }) {
  return (
    <section className="course-detail-instructor border-y border-ink/10 py-20 md:py-28" data-testid="course-detail-instructor">
      <div className="site-content-shell grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-16">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} className="lg:col-span-3">
          <div data-theme-surface="dark" className="aspect-square overflow-hidden rounded-2xl border bg-ink/[0.02]" style={{ borderColor: `${course.accent}55` }}>
            {course.instructor.headshotUrl ? (
              <img src={course.instructor.headshotUrl} alt={course.instructor.portraitAlt} className="h-full w-full object-cover" />
            ) : (
              <div className="grid h-full w-full place-items-center">
                <span className="font-serif text-6xl" style={{ color: `color-mix(in srgb, ${course.accent} var(--accent-strength, 100%), black)` }}>{course.instructor.initials}</span>
              </div>
            )}
          </div>
        </motion.div>
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} className="lg:col-span-9">
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-tone-500">Prepared by</span>
          <h3 className="mt-4 font-serif text-3xl text-ink md:text-4xl">{course.instructor.profilePath ? <Link to={course.instructor.profilePath}>{course.instructor.name}</Link> : course.instructor.name}</h3>
          <div className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em]" style={{ color: `color-mix(in srgb, ${course.accent} var(--accent-strength, 100%), black)` }}>{course.instructor.role}</div>
          <p className="mt-6 max-w-3xl text-lg leading-relaxed text-tone-300">{course.instructor.bio}</p>
          {course.additionalSpeakers.length ? <p className="mt-4 text-tone-300">Also featuring: {course.additionalSpeakers.map((person, index) => <span key={person.id}>{index > 0 ? ", " : ""}<Link className="text-accent-ink" to={`/speakers/${person.slug}`}>{person.name}</Link></span>)}</p> : null}
        </motion.div>
      </div>
    </section>
  );
}

function OutlineBand({ course }) {
  return (
    <section className="course-detail-library site-content-shell py-20 md:py-28" data-testid="course-detail-outline-band">
      <motion.div variants={staggerContainer} initial="hidden" whileInView="show" viewport={viewportOnce} className="glass grid grid-cols-1 items-center gap-10 rounded-2xl p-8 md:p-12 lg:grid-cols-2">
        <motion.div variants={fadeUp}>
          <h2 className="font-serif text-3xl font-light leading-tight text-ink md:text-5xl">Explore the <span className="italic" style={{ color: `color-mix(in srgb, ${course.accent} var(--accent-strength, 100%), black)` }}>course library</span>.</h2>
          <p className="mt-4 max-w-xl leading-relaxed text-tone-400">This is a {course.isDemo ? 'demo course' : 'course'} outline. Enrollment, payment and completion tracking are not enabled.</p>
        </motion.div>
        <motion.div variants={fadeUp} className="space-y-6">
          <FlowButton to="/webinars#webinar-series" className="block text-center w-full py-4 font-mono text-xs uppercase tracking-[0.25em]  transition-transform hover:-translate-y-0.5" >Browse course outlines</FlowButton>
        </motion.div>
      </motion.div>
    </section>
  );
}

export function buildCourse(webinar, index) {
  const covered = splitList(webinar.covered || webinar.whatCovered || webinar.agenda || webinar.topics);
  const outcomes = (webinar.outcomes?.length ? webinar.outcomes : covered.length ? covered : [
    webinar.audience,
    webinar.takeaway,
    'How to review the issue before it becomes urgent.',
    'How to assign ownership and follow-up.',
    'How to make the process easier to repeat.',
  ]).filter(Boolean);
  const speaker = webinar.speaker || {};
  const speakerName = speakerDisplayName(speaker) || 'Magellan';
  const speakerRole = [speaker.title, speaker.company].filter(Boolean).join(' / ') || 'Course instructor';
  const isDemo = webinar.isDemo === true;
  const categoryLabel = COURSE_CATEGORIES.find(({ id }) => id === webinar.category)?.label || 'Course';
  const theme = resolveCourseDetailPresentation(webinar);

  const syllabus = Array.isArray(webinar.modules) ? webinar.modules.filter(module => module.public !== false).map((module, index) => ({
    module: `Module ${String(index + 1).padStart(2, '0')}`, title: module.title, desc: module.description || '',
    lessons: (module.lessons || []).filter(lesson => typeof lesson === 'string' || lesson.public !== false).map(lesson => typeof lesson === 'string' ? { title: lesson } : lesson), free: true,
  })) : outcomes.map((item, index) => ({ module: `Topic ${String(index + 1).padStart(2, '0')}`, title: item, desc: 'Course outline', lessons: [{ title: item }], free: true }));
  return {
    slug: webinar.slug,
    isDemo,
    categoryLabel,
    categoryHeading: webinar.category === 'courses' ? 'Course' : webinar.category === 'webinars' ? 'Webinar' : categoryLabel,
    title: webinar.title || 'Magellan course',
    tagline: webinar.description || webinar.shortText || webinar.takeaway || 'A practical Magellan course for trucking company operating structure.',
    level: webinar.level || 'Practical',
    duration: isDemo ? 'Demo outline' : 'Course outline',
    moduleCount: syllabus.length,
    lessonCount: syllabus.reduce((count, module) => count + module.lessons.length, 0),
    accent: theme.accent,
    theme,
    themeLabel: theme.label,
    visualTemplate: theme.visualTemplate,
    thumbnailAlt: webinar.heroImageUrl ? webinar.heroImageAlt : webinar.featuredImageAlt,
    thumbnail: webinar.heroImageUrl || webinar.featuredImageUrl || WEBINAR_FALLBACK_IMAGE,
    previewVideo: webinar.previewVideoEnabled ? (webinar.previewVideoUrl || webinar.previewYoutubeEmbedUrl || '') : '',
    previewVideoProvider: webinar.previewVideoProvider || '',
    previewLesson: webinar.previewVideoTitle || `${webinar.title || 'Course'} preview`,
    additionalSpeakers: (webinar.speakers || []).filter(person => person.id !== speaker.id),
    instructor: {
      name: speakerName,
      role: speakerRole,
      initials: getInitials(speakerName),
      headshotUrl: speaker.headshotUrl || '',
      portraitAlt: speaker.portraitAlt || speakerName,
      profilePath: speaker.slug ? `/speakers/${speaker.slug}` : '',
      bio: speaker.shortBio || speaker.bio || speaker.fullBio || 'Magellan training focuses on practical operating structure, documentation, follow-up, and repeatable work.',
    },
    description: [
      webinar.description || webinar.shortText || 'This course is built around the operating pressure trucking companies usually notice too late.',
      webinar.takeaway || 'You will leave with a clearer way to review the issue, assign ownership, and decide what should happen next.',
    ].filter(Boolean),
    outcomes,
    forWho: [
      webinar.audience,
      'Owners and managers responsible for the operating structure behind the freight.',
      'Safety, compliance, recruiting, dispatch, admin, and training teams that need repeatable work.',
    ].filter(Boolean),
    syllabus,
    index,
    registrationId: webinar.id || webinar.slug || getWebinarSlug(webinar),
  };
}

export default function WebinarCourseDetailPage({ previewRecord }) {
  const location = useLocation();
  return <CourseDetail key={JSON.stringify([location.pathname, previewRecord?.id])} previewRecord={previewRecord} />;
}
function CourseDetail({ previewRecord }) {
  const { slug: routeSlug } = useParams();
  const location = useLocation();
  const slug = routeSlug || location.pathname.split('/').filter(Boolean).pop();
  const [preview, setPreview] = useState(false);
  const [webinars, setWebinars] = useState([]);
  const [webinar, setWebinar] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');

  useEffect(() => {
    let controller;
    setStatus('loading'); setError(''); setWebinar(null); setPreview(false);
    const load = () => {
      controller?.abort();
      const request = new AbortController();
      controller = request;
      Promise.all([previewRecord ? Promise.resolve(previewRecord) : getPublicCourseBySlug(slug, request.signal), listPublicCourses(request.signal).catch(() => null)]).then(([record, catalog]) => {
        if (request.signal.aborted) return;
        setWebinar(record); if (catalog) setWebinars(catalog); setError(''); setStatus('ready');
      }).catch((failure) => {
        if (request.signal.aborted) return;
        setError(failure.message || 'This course is temporarily unavailable.'); setStatus('error');
      });
    };
    load();
    const stop = previewRecord ? () => {} : watchCourseContentChanges(load);
    return () => { stop(); controller?.abort(); };
  }, [slug, previewRecord]);

  const course = useMemo(() => webinar ? buildCourse(webinar, Math.max(0, webinars.findIndex((item) => item.id === webinar.id))) : null, [webinar, webinars]);
  const nextWebinar = webinars.length > 1 ? webinars[(webinars.findIndex((item) => item.id === webinar?.id) + 1) % webinars.length] : null;
  const nextSlug = nextWebinar?.slug || '';

  if (status === 'error') return <div className="min-h-screen bg-canvas px-6 pt-32 text-center text-ink"><p role="alert" className="font-body text-copy/60">{error}</p><Link to="/webinars" className="mt-6 inline-block text-accent-ink">Back to course library</Link></div>;
  if (status === 'ready' && !course) return <Navigate to="/webinars" replace />;
  if (!course) {
    return (
      <div className="min-h-screen bg-canvas pt-32 text-center text-ink">
        <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-accent-ink">Loading course</span>
      </div>
    );
  }

  const summaryUnits = [
    { v: course.moduleCount, l: 'Sections' },
    { v: course.lessonCount, l: 'Topics' },
    { v: course.isDemo ? 'Demo' : 'Outline', l: 'Access' },
    { v: course.index + 1, l: 'No' },
  ];
  const totalCourses = webinars.length || 1;

  return (
    <div className="course-detail-page text-ink" data-course-presentation="panels" style={{ '--course-light-panel': course.theme.lightPanelBg, '--course-detail-accent': course.accent, '--course-detail-secondary': course.theme.secondaryAccent || course.accent }} data-testid={`webinar-course-page-${course.slug}`} data-theme={course.theme.key}>

      <section className="course-detail-hero relative pt-32 md:pt-40 pb-20 overflow-hidden grain">
        <GridGlow accent={course.accent} />
        <Watermark className="bottom-0 right-[-2%] text-[24vw] hidden lg:block">{String(course.index + 1).padStart(2, '0')}</Watermark>
        <div className="course-detail-hero-layout site-content-shell relative grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="course-detail-heading lg:col-span-7">
            <motion.span initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="font-mono text-[11px] uppercase tracking-[0.3em]" style={{ color: `color-mix(in srgb, ${course.accent} var(--accent-strength, 100%), black)` }}>
              {course.categoryHeading}
            </motion.span>
            <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 1, ease: [0.16, 1, 0.3, 1] }} className="mt-5 font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[0.92] tracking-tighter">
              {course.title}
            </motion.h1>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="mt-6 text-lg md:text-xl text-tone-300 leading-relaxed max-w-xl">
              {course.tagline}
            </motion.p>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }} className="course-detail-chips mt-8 flex flex-wrap gap-2.5">
              {[
                { icon: GraduationCap, t: course.level },
                { icon: Clock, t: course.duration },
                { icon: BarChart3, t: `${course.moduleCount} outline sections` },
                { icon: Check, t: `${course.lessonCount} topics` },
              ].map((chip) => {
                const Icon = chip.icon;
                return (
                  <span key={chip.t} className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-tone-300 border border-ink/15 rounded-full px-4 py-2">
                    <Icon size={13} style={{ color: `color-mix(in srgb, ${course.accent} var(--accent-strength, 100%), black)` }} /> {chip.t}
                  </span>
                );
              })}
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} className="course-detail-byline mt-10 flex items-center gap-4">
              <span className="grid place-items-center h-12 w-12 rounded-full font-serif text-lg border" style={{ borderColor: course.accent, color: `color-mix(in srgb, ${course.accent} var(--accent-strength, 100%), black)` }}>
                {course.instructor.initials}
              </span>
              <div>
                <div className="font-serif text-lg text-ink">{course.instructor.name}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-tone-500">{course.instructor.role}</div>
              </div>
            </motion.div>
            {course.theme.key !== 'data-room' && <CourseCounterRail current={course.index + 1} total={totalCourses} course={course} />}
          </div>

          <div className="course-detail-summary-column lg:col-span-5">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.9, ease: [0.16, 1, 0.3, 1] }} className="course-detail-summary lg:sticky lg:top-28 glass rounded-2xl p-6 space-y-6">
              {course.previewVideo ? <CourseThumb thumb={course.thumbnail} alt={course.thumbnailAlt} accent={course.accent} onPlay={() => setPreview(true)} data-testid="course-detail-thumb" /> : <div data-theme-surface="dark" className="relative aspect-[4/3] w-full overflow-hidden rounded-xl border border-ink/10 bg-black" data-testid="course-detail-thumb"><img src={course.thumbnail} alt={course.thumbnailAlt || ""} className="absolute inset-0 h-full w-full object-cover opacity-80" /><div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" /><span className="absolute bottom-5 left-5 font-mono text-[10px] uppercase tracking-[0.25em] text-copy/70">{course.isDemo ? 'Demo course outline' : 'Course outline'}</span></div>}
              <p className="font-body text-sm leading-relaxed text-tone-400">One recorded course is $299 per person; the current ten-course series is $1,499 per person. Recorded videos include accompanying PDF notes, checklists, forms or worked examples, as applicable to the subject. Access lasts three months from the moment Magellan receives payment, for one person. Later course additions are not included in the current ten-course series. Enrollment and payment are not enabled.</p>
              <div className="course-detail-summary-stats grid grid-cols-4 gap-2">
                {summaryUnits.map((unit) => (
                  <div key={unit.l} className="text-center bg-surface/40 border border-ink/5 rounded-lg py-3">
                    <div className="font-mono text-2xl md:text-3xl text-ink tabular-nums">{String(unit.v).padStart(2, '0')}</div>
                    <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-tone-500 mt-1">{unit.l}</div>
                  </div>
                ))}
              </div>
              <FlowButton as="a" href="#course-curriculum" className="block text-center w-full  py-4 font-mono text-xs uppercase tracking-[0.25em] transition-transform hover:-translate-y-0.5" >Explore curriculum</FlowButton>
              <Link to="/paperwork" className="block text-center font-mono text-[10px] uppercase tracking-[0.2em] text-tone-400 transition-colors hover:text-ink">
                Need the full paperwork system? View Paperwork Packages
              </Link>
              {course.theme.key === 'data-room' && <CourseCounterRail current={course.index + 1} total={totalCourses} course={course} />}
            </motion.div>
          </div>
        </div>
      </section>

      {webinar.enrollmentEnabled && !previewRecord && <div className="site-content-shell pb-12"><FlowButton to={'/customer/access/enroll/'+webinar.id} className="inline-flex  px-7 py-4 font-mono text-xs uppercase tracking-widest ">{webinar.offer?.mode === 'free' ? 'Register for free' : 'Buy course'}</FlowButton></div>}
      <ThemeSignalStrip course={course} />

      <section className="course-detail-outcomes site-content-shell py-20 md:py-28 border-t border-ink/10">
        <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} className="font-serif font-light text-3xl md:text-5xl tracking-tight mb-12">
          What you&rsquo;ll <span className="italic" style={{ color: `color-mix(in srgb, ${course.accent} var(--accent-strength, 100%), black)` }}>learn</span>
        </motion.h2>
        <motion.div variants={staggerContainer} initial="hidden" whileInView="show" viewport={viewportOnce} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-6">
          {course.outcomes.map((outcome) => (
            <motion.div key={outcome} variants={fadeUp} className="flex items-start gap-4 border-b border-ink/10 pb-5">
              <Check size={18} className="mt-1 shrink-0" style={{ color: `color-mix(in srgb, ${course.accent} var(--accent-strength, 100%), black)` }} />
              <span className="text-tone-200 leading-relaxed">{outcome}</span>
            </motion.div>
          ))}
        </motion.div>
      </section>

      <section className="course-detail-about border-y border-ink/10 py-20 md:py-28">
        <div className="site-content-shell grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} className="lg:col-span-7">
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-tone-500">About this course</span>
            {course.description.map((paragraph) => (
              <p key={paragraph} className="mt-5 text-lg md:text-2xl text-tone-300 leading-relaxed font-light">{paragraph}</p>
            ))}
          </motion.div>
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} className="lg:col-span-5">
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-tone-500">Who it&rsquo;s for</span>
            <ul className="mt-6 space-y-4">
              {course.forWho.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <Users size={16} className="mt-1 shrink-0" style={{ color: `color-mix(in srgb, ${course.accent} var(--accent-strength, 100%), black)` }} />
                  <span className="text-tone-200 leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      <section id="course-curriculum" className="course-detail-curriculum site-content-shell py-20 md:py-28 scroll-mt-28">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} className="mb-10 flex items-end justify-between">
          <h2 className="font-serif font-light text-3xl md:text-5xl tracking-tight">Curriculum</h2>
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-tone-500">{course.moduleCount} outline sections</span>
        </motion.div>

        {webinar.kind === 'bundle' && webinar.bundleCourseIds?.length ? <ul className="course-detail-bundle mb-8 space-y-3">{webinar.bundleCourseIds.map(id => webinars.find(item => item.id === id)).filter(Boolean).map(member => <li key={member.id}><Link className="text-accent-ink" to={`/webinars/${member.slug}`}>{member.title}</Link></li>)}</ul> : null}
        <Accordion type="single" collapsible defaultValue="item-0" className="w-full" data-testid="syllabus-accordion">
          {course.syllabus.map((mod, index) => (
            <AccordionItem key={mod.module} value={`item-${index}`} className="border-b border-ink/10" data-testid={`syllabus-item-${index}`}>
              <AccordionTrigger className="py-7 hover:no-underline group">
                <div className="flex items-start gap-5 text-left">
                  <span className="font-mono text-xs text-tone-500 pt-2">{mod.module}</span>
                  <div>
                    <span className="font-serif text-2xl md:text-3xl text-ink transition-colors group-hover:opacity-80 block">{mod.title}</span>
                    <span className="text-tone-500 text-sm mt-1 block">{mod.desc}</span>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-7">
                <ul className="space-y-3 pl-[4.7rem]">
                  {mod.lessons.map((lesson, lessonIndex) => (
                    <li key={lessonIndex} className="flex items-center gap-3 text-tone-300">
                      {mod.free ? <Check className="h-4 w-4 shrink-0" style={{ color: `color-mix(in srgb, ${course.accent} var(--accent-strength, 100%), black)` }} /> : <Lock className="h-3.5 w-3.5 text-tone-600 shrink-0" />}
                      <span className="leading-relaxed">{lesson.title}{lesson.description ? <span className="block text-sm text-tone-400">{lesson.description}</span> : null}</span>
                      {course.previewVideo && mod.free && lessonIndex === 0 ? (
                        <button type="button" data-testid="lesson-preview-trigger" onClick={() => setPreview(true)} className="ml-2 font-mono text-[10px] uppercase tracking-widest hover:underline" style={{ color: `color-mix(in srgb, ${course.accent} var(--accent-strength, 100%), black)` }}>
                          Preview
                        </button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <InstructorSection course={course} />

      <OutlineBand course={course} />

      <section className="course-detail-next bg-canvas py-20 md:py-28 border-t border-ink/10">
        <div className="site-content-shell text-center">
          {nextSlug ? (
            <Link to={`/webinars/${nextSlug}`} data-testid="course-detail-next" className="group inline-flex flex-col items-center">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-tone-500">Next course</span>
              <span className="mt-3 font-serif text-3xl md:text-5xl text-ink group-hover:text-accent-ink transition-colors flex items-center gap-3">
                {nextWebinar.title}
                <ArrowRight size={28} className="group-hover:translate-x-2 transition-transform" />
              </span>
            </Link>
          ) : null}
        </div>
      </section>

      {typeof document !== 'undefined' ? createPortal(
        <AnimatePresence>{preview ? (
          <motion.div data-testid="course-detail-modal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setPreview(false)} className="fixed inset-0 z-[80] grid place-items-center overflow-y-auto bg-black/88 p-3 backdrop-blur-md sm:p-6">
            <motion.div initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }} onClick={(event) => event.stopPropagation()} data-theme-surface="dark" className="relative my-auto w-full max-w-5xl border border-ink/10 bg-black/70 p-3 shadow-[0_30px_120px_rgba(0,0,0,0.55)] sm:p-4">
              <button type="button" data-testid="course-detail-close" onClick={() => setPreview(false)} className="mb-3 ml-auto flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-copy/70 hover:text-ink">
                Close <X size={18} />
              </button>
              <div data-theme-surface="dark" className="aspect-video w-full overflow-hidden rounded-xl border border-ink/10 bg-black">
                {course.previewVideo && course.previewVideoProvider === 'upload' ? (
                  <video
                    title={course.previewLesson}
                    src={course.previewVideo}
                    className="h-full w-full bg-black object-cover"
                    controls
                    controlsList="nodownload"
                    preload="metadata"
                    playsInline
                    poster={course.thumbnail || undefined}
                  />
                ) : course.previewVideo ? (
                  <iframe title={course.previewLesson} src={course.previewVideo} className="h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen" allowFullScreen />
                ) : (
                  <img src={course.thumbnail} alt={course.thumbnailAlt || ""} className="h-full w-full object-cover opacity-70" />
                )}
              </div>
              <p className="mt-4 font-serif text-2xl text-ink">{course.previewLesson}</p>
            </motion.div>
          </motion.div>
        ) : null}</AnimatePresence>, document.body,
      ) : null}
    </div>
  );
}
